import { hexLen, hexToWorld, type Hex } from '../hex.ts';
import { idiv, isqrt } from '../math/fixed.ts';
import { deriveSeed, rngInt, seedRng, type RngState } from '../math/rng.ts';

/**
 * Tormo (docs/sendas/artifice.md, chapter 5): the grounds, the same from the seed in the sim and the drawing, integers
 * only. Up is −y (the top of the screen).
 *
 * - `kneesLayout` (act 1): Tormo's hall coming down. A ring of floor round fallen rubble in the middle; Dunna leads Nadd
 *   round it by the hand, Tormo walks it after them. His four joints (`kneesJoints`): a train of gears in a row each,
 *   two or three free axles touching two of its gears; one of them touches two that turn opposite ways.
 * - `tornoLayout` (act 2): the winch house's ledge under Grada's wall, the Raja over it. The box's four slots, the shelf
 *   with its five gears (`tornoSet`: the slowest the shelf gives takes both stages), the lever; the cable from the drum
 *   up the wall to Tormo, the abyss to the south-east where the gargoyles come from.
 * - `shellLayout` (act 3, the test): the workshop's yard, Tormo tied in its middle; Nadd's four drills round him, the
 *   chest's drill before him, the belts' levers and the mill. `shellSet` picks the forces, arrows, belts, which drill the
 *   chest's goes with and which stone each drill reaches with Tormo turned each way; `shellSolve` counts the ways.
 * - `platesLayout` (act 4): the same yard, open: Tormo in it, Nadd at the forge's door with the quarrymen before him,
 *   the three crossbows round it.
 */

export const KNEES_RADIUS = 11;
export const TORNO_RADIUS = 8;
export const SHELL_RADIUS = 8;
export const PLATES_RADIUS = 10;

const allCells = (R: number): Hex[] => {
  const out: Hex[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};
const key = (h: Hex): string => `${h.q},${h.r}`;
const sortHex = (a: Hex, b: Hex): number => a.r - b.r || a.q - b.q;
type Pt = { x: number; y: number };

const shuffle = <T>(rng: RngState, a: T[]): T[] => {
  for (let i = a.length - 1; i > 0; i--) {
    const j = rngInt(rng, i + 1);
    const t = a[i] as T;
    a[i] = a[j] as T;
    a[j] = t;
  }
  return a;
};

/** The ring of hexes `R` from the middle, in order round it (starting at its south-west corner). */
export const hexRing = (R: number): Hex[] => {
  const dirs: Hex[] = [
    { q: 1, r: 0 },
    { q: 1, r: -1 },
    { q: 0, r: -1 },
    { q: -1, r: 0 },
    { q: -1, r: 1 },
    { q: 0, r: 1 },
  ];
  const out: Hex[] = [];
  let c: Hex = { q: -R, r: R };
  for (const d of dirs)
    for (let j = 0; j < R; j++) {
      out.push(c);
      c = { q: c.q + d.q, r: c.r + d.r };
    }
  return out;
};

/* ---------- the knees ---------- */

export interface KneeJointDef {
  off: Pt;
  gears: number;
  drive: number;
  free: { a: number; b: number }[];
  jam: number;
}

export interface KneesLayout {
  radius: number;
  rocks: Hex[];
  floor: Hex[];
  /** The rubble fallen in the middle (rocks too). */
  rubble: Hex[];
  ring: Hex[];
  way: Pt[];
  len: number[];
  total: number;
  joints: KneeJointDef[];
  holes: Pt[];
  /** The heaps of gears by the wall, round the hall. */
  piles: Pt[];
  start: Pt;
}

/** Where each joint is on Tormo (his frame: x to his right, y ahead): the knees low and close, the elbows out. */
export const KNEE_OFFS: readonly Pt[] = [
  { x: -480, y: 260 },
  { x: 480, y: 260 },
  { x: -1080, y: -140 },
  { x: 1080, y: -140 },
];

/**
 * The four joints' trains: the knees four gears and two or three free axles, the elbows five and three. One free axle
 * touches two gears an odd number apart (they turn opposite ways: a gear there jams it); the others an even number
 * apart (both the same way: a gear there only spins). The jam sits next door or three apart, so it takes counting.
 */
export const kneesJoints = (seed: number): KneeJointDef[] => {
  const rng = seedRng(deriveSeed(seed, 0x6b6e));
  return KNEE_OFFS.map((off, j) => {
    const gears = j < 2 ? 4 : 5;
    const k = j < 2 ? 2 + rngInt(rng, 2) : 3;
    const odd: { a: number; b: number }[] = [];
    const even: { a: number; b: number }[] = [];
    for (let a = 0; a < gears; a++)
      for (let b = a + 1; b < gears; b++) {
        if ((b - a) % 2 === 1 && (b - a === 1 || b - a === 3)) odd.push({ a, b });
        if ((b - a) % 2 === 0) even.push({ a, b });
      }
    // Three apart more often than next door (next door, a ring of three, shows itself).
    const far = odd.filter((p) => p.b - p.a === 3);
    const jamPair = (far.length && rngInt(rng, 3) > 0 ? far[rngInt(rng, far.length)] : odd[rngInt(rng, odd.length)]) as { a: number; b: number };
    const decoys = shuffle(rng, even.slice()).slice(0, k - 1);
    const free = shuffle(rng, [jamPair, ...decoys]);
    return { off, gears, drive: rngInt(rng, 2) === 0 ? 1 : -1, free, jam: free.indexOf(jamPair) };
  });
};

const segLen = (a: Pt, b: Pt): number => isqrt((b.x - a.x) * (b.x - a.x) + (b.y - a.y) * (b.y - a.y));

export const kneesLayout = (seed: number): KneesLayout => {
  const R = KNEES_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0x6b6c));
  const ring = hexRing(6);
  const way = ring.map((h) => hexToWorld(h.q, h.r));
  const len: number[] = [0];
  for (let i = 1; i <= way.length; i++) len.push((len[i - 1] as number) + segLen(way[i - 1] as Pt, way[i % way.length] as Pt));
  const total = len[way.length] as number;
  // Rubble in the middle: the roof's beams come down; the ring and a hex inside it stay clear.
  const inner = allCells(3).filter((h) => hexLen(h.q, h.r) >= 1);
  const rubble = shuffle(rng, inner).slice(0, 7).sort(sortHex);
  const solid = new Set(rubble.map(key));
  const floor = allCells(R).filter((h) => hexLen(h.q, h.r) <= R - 2 && !solid.has(key(h)));
  const open = new Set(floor.map(key));
  const rocks = allCells(R + 2).filter((h) => !open.has(key(h)));
  const holes = [hexToWorld(-8, 3), hexToWorld(8, -4), hexToWorld(2, -8), hexToWorld(-3, 8)];
  const s = hexToWorld(-4, 4);
  return {
    piles: [hexToWorld(-7, 3), hexToWorld(7, -3), hexToWorld(-2, -6), hexToWorld(2, 6)],
    radius: R,
    rocks: rocks.sort(sortHex),
    floor: floor.sort(sortHex),
    rubble,
    ring,
    way,
    len,
    total,
    joints: kneesJoints(seed),
    holes,
    start: { x: s.x, y: s.y },
  };
};

/* ---------- the winch ---------- */

export interface TornoSet {
  pieces: number[];
  wantNum: number;
  wantDen: number;
}

const SHELVES: readonly (readonly number[])[] = [
  [12, 12, 24, 36, 36],
  [12, 12, 18, 36, 36],
  [12, 18, 24, 36, 36],
  [12, 12, 24, 24, 36],
  [12, 18, 18, 36, 36],
  [12, 12, 18, 24, 36],
];

const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

/** Every way of setting four of the shelf's gears in A, B1, B2, C: their indices and the drum's speed as a fraction. */
export const tornoWays = (pieces: readonly number[], input: number): { set: number[]; num: number; den: number }[] => {
  const out: { set: number[]; num: number; den: number }[] = [];
  const n = pieces.length;
  for (let a = 0; a < n; a++)
    for (let b1 = 0; b1 < n; b1++)
      for (let b2 = 0; b2 < n; b2++)
        for (let c = 0; c < n; c++) {
          if (a === b1 || a === b2 || a === c || b1 === b2 || b1 === c || b2 === c) continue;
          out.push({ set: [a, b1, b2, c], num: input * (pieces[a] as number) * (pieces[b2] as number), den: (pieces[b1] as number) * (pieces[c] as number) });
        }
  return out;
};

/** The shelf (shuffled) and the slowest it gives: what Tormo's mark asks. */
export const tornoSet = (seed: number, input = 60): TornoSet => {
  const rng = seedRng(deriveSeed(seed, 0x7a70));
  const pieces = shuffle(rng, (SHELVES[rngInt(rng, SHELVES.length)] as number[]).slice());
  let best = { num: 1, den: 0 };
  for (const w of tornoWays(pieces, input)) if (best.den === 0 || w.num * best.den < best.num * w.den) best = { num: w.num, den: w.den };
  const g = gcd(best.num, best.den);
  return { pieces, wantNum: idiv(best.num, g), wantDen: idiv(best.den, g) };
};

export interface TornoLayout {
  radius: number;
  rocks: Hex[];
  floor: Hex[];
  slots: Hex[];
  lie: Hex[];
  lever: Hex;
  drum: Pt;
  top: Pt;
  /** Where the gargoyles sit on the cable, from the drum up. */
  perches: Pt[];
  from: Pt;
  dunnaAt: Pt;
  start: Pt;
  set: TornoSet;
}

export const tornoLayout = (seed: number): TornoLayout => {
  const R = TORNO_RADIUS;
  const slots: Hex[] = [
    { q: -3, r: 0 },
    { q: -1, r: 0 },
    { q: 0, r: -1 },
    { q: 2, r: -1 },
  ];
  const lie: Hex[] = [
    { q: -4, r: 2 },
    { q: -3, r: 2 },
    { q: -2, r: 2 },
    { q: -1, r: 2 },
    { q: 0, r: 2 },
  ];
  const lever: Hex = { q: 4, r: -1 };
  const drumHex: Hex = { q: 3, r: -2 };
  const solid = new Set([...slots, drumHex].map(key));
  // The ledge: under the wall (north) and over the drop (south), wider than deep.
  const floor = allCells(R).filter((h) => hexLen(h.q, h.r) <= R - 1 && h.r >= -3 && h.r <= 4 && !solid.has(key(h)));
  const open = new Set(floor.map(key));
  const rocks = allCells(R + 2).filter((h) => !open.has(key(h)));
  const drum = hexToWorld(drumHex.q, drumHex.r);
  const top = { x: drum.x - 1800, y: -11000 };
  const perches: Pt[] = [];
  // (On the cable's first stretch only: every one within a hero's shot from the ledge's north edge. One higher up drew
  // the hero after it into the wall, where it stood stuck under the Raja's stones.)
  const low = { x: drum.x + idiv((top.x - drum.x) * 3, 10), y: -5000 };
  for (let i = 0; i < 6; i++) perches.push({ x: drum.x + idiv((low.x - drum.x) * (i + 1), 6), y: drum.y + idiv((low.y - drum.y) * (i + 1), 6) });
  const d = hexToWorld(3, 2);
  const s = hexToWorld(0, 3);
  return {
    radius: R,
    rocks: rocks.sort(sortHex),
    floor: floor.sort(sortHex),
    slots,
    lie,
    lever,
    drum,
    top,
    perches,
    from: { x: 7000, y: 5000 },
    dunnaAt: { x: d.x, y: d.y },
    start: { x: s.x, y: s.y },
    set: tornoSet(seed),
  };
};

/* ---------- the shell ---------- */

export interface ShellSet {
  force: number[];
  right0: boolean[];
  gang: number;
  need: number[];
  reach: number[][];
}

export const SHELL_DRILLS = 4;
export const SHELL_STONES = 3;

/** Whether drill `d` turns its arrow's way with the belts so. */
export const shellRight = (set: Pick<ShellSet, 'right0'>, belts: readonly number[], d: number): boolean => (set.right0[d] ?? false) !== (belts[d < 2 ? 0 : 1] === 1);

/**
 * The ways to bring stone `k` down with Tormo on `side`: the drills in (a bit each) and the belts (I, II), each drill in
 * cutting it (none in for nothing). Valid: its force reached, the mill's 3 not passed (the chest's drill asking its own
 * with the gang's in), the chest's not cutting (the gang's turning its arrow's way: the chest's, on its belt the other
 * way, turns back). `blind`: as one who doesn't see the chest's drill (its ask and its cut left out).
 */
export const shellWays = (set: ShellSet, side: number, k: number, mill = 3, chest = 1, blind = false): { inn: number; belts: number[] }[] => {
  const out: { inn: number; belts: number[] }[] = [];
  const reach = set.reach[side] as number[];
  for (let inn = 1; inn < 16; inn++)
    for (let b = 0; b < 4; b++) {
      const belts = [b & 1, b >> 1];
      let force = 0;
      let ask = 0;
      let idle = false;
      for (let d = 0; d < SHELL_DRILLS; d++) {
        if (!(inn & (1 << d))) continue;
        const f = set.force[d] as number;
        ask += f;
        if (reach[d] === k && shellRight(set, belts, d)) force += f;
        else idle = true;
      }
      if (idle || force < (set.need[k] as number)) continue;
      const gangIn = !!(inn & (1 << set.gang));
      if (!blind && gangIn) {
        ask += chest;
        if (!shellRight(set, belts, set.gang)) continue;
      }
      if (ask > mill) continue;
      out.push({ inn, belts });
    }
  return out;
};

/** Whether a set makes a good test: every stone can come down; one only with the chest's drill in (backwards); the blind jam or crack on it. */
export const shellGood = (set: ShellSet): boolean => {
  let lesson = false;
  for (let k = 0; k < SHELL_STONES; k++) {
    const ways = [0, 1].flatMap((side) => shellWays(set, side, k).map((w) => ({ ...w, side })));
    if (!ways.length || ways.length > 4) return false;
    const blind = [0, 1].flatMap((side) => shellWays(set, side, k, 3, 1, true).map((w) => ({ ...w, side })));
    const gangAll = ways.every((w) => !!(w.inn & (1 << set.gang)));
    // A blind way that isn't one: the chest's ask jams it, or the chest cuts.
    const trap = blind.some((b) => !ways.some((w) => w.side === b.side && w.inn === b.inn && w.belts[0] === b.belts[0] && w.belts[1] === b.belts[1]));
    if (gangAll && trap) lesson = true;
  }
  // Each side has something to bring down (no window wasted wholly).
  for (const side of [0, 1]) if (![0, 1, 2].some((k) => shellWays(set, side, k).length)) return false;
  return lesson;
};

export const shellSet = (seed: number): ShellSet => {
  const rng = seedRng(deriveSeed(seed, 0x5e11));
  for (let tries = 0; tries < 2000; tries++) {
    const force = [0, 1, 2, 3].map(() => 1 + rngInt(rng, 2));
    if (force.reduce((a, b) => a + b, 0) < 5) continue;
    const right0 = [0, 1, 2, 3].map(() => rngInt(rng, 2) === 0);
    const gang = rngInt(rng, SHELL_DRILLS);
    const need = [0, 1, 2].map(() => 2 + rngInt(rng, 2));
    const reach = [0, 1].map(() => shuffle(rng, [0, 1, 2, rngInt(rng, 4) - 1]));
    const set = { force, right0, gang, need, reach };
    if (shellGood(set)) return set;
  }
  // (Never reached in the tests' seeds: a hand-made one that holds.)
  return {
    force: [2, 1, 2, 1],
    right0: [true, false, true, true],
    gang: 0,
    need: [2, 3, 3],
    reach: [
      [0, 2, 2, 1],
      [1, 0, 1, 0],
    ],
  };
};

export interface ShellLayout {
  radius: number;
  rocks: Hex[];
  floor: Hex[];
  tormo: Hex[];
  tormoAt: Pt;
  drills: Hex[];
  spots: Hex[];
  chest: Hex;
  levers: Hex[];
  mill: Hex;
  stones: Pt[];
  holes: Pt[];
  dunnaAt: Pt;
  start: Pt;
  set: ShellSet;
}

export const shellLayout = (seed: number): ShellLayout => {
  const R = SHELL_RADIUS;
  // Tormo kneeling tied in the middle: three hexes of him.
  const tormo: Hex[] = [
    { q: 0, r: -1 },
    { q: 1, r: -1 },
    { q: 0, r: 0 },
  ];
  const t0 = hexToWorld(0, -1);
  const t1 = hexToWorld(1, -1);
  const t2 = hexToWorld(0, 0);
  const tormoAt = { x: idiv(t0.x + t1.x + t2.x, 3), y: idiv(t0.y + t1.y + t2.y, 3) };
  // The drills: two on his west, two on his east, each on its frame; the clutch's spot a hex outward.
  const drills: Hex[] = [
    { q: -2, r: -2 },
    { q: -3, r: 1 },
    { q: 3, r: -3 },
    { q: 3, r: 0 },
  ];
  const spots: Hex[] = [
    { q: -3, r: -2 },
    { q: -4, r: 1 },
    { q: 4, r: -3 },
    { q: 4, r: 0 },
  ];
  const chest: Hex = { q: 0, r: 2 };
  const levers: Hex[] = [
    { q: -2, r: 4 },
    { q: 1, r: 4 },
  ];
  const mill: Hex = { q: -5, r: 5 };
  const solid = new Set([...tormo, ...drills, chest, mill].map(key));
  const floor = allCells(R).filter((h) => hexLen(h.q, h.r) <= R - 1 && !solid.has(key(h)));
  const open = new Set(floor.map(key));
  const rocks = allCells(R + 2).filter((h) => !open.has(key(h)));
  // The stones (left shoulder, right shoulder, back) with Tormo facing south (his side 0 to the west line).
  const stones: Pt[] = [
    { x: tormoAt.x - 900, y: tormoAt.y - 200 },
    { x: tormoAt.x + 900, y: tormoAt.y - 200 },
    { x: tormoAt.x, y: tormoAt.y - 800 },
  ];
  const d = hexToWorld(-4, 5);
  const s = hexToWorld(0, 5);
  return {
    radius: R,
    rocks: rocks.sort(sortHex),
    floor: floor.sort(sortHex),
    tormo,
    tormoAt,
    drills,
    spots,
    chest,
    levers,
    mill,
    stones,
    holes: [hexToWorld(-6, -1), hexToWorld(6, -4), hexToWorld(5, 2), hexToWorld(-5, 3)],
    dunnaAt: { x: d.x, y: d.y },
    start: { x: s.x, y: s.y },
    set: shellSet(seed),
  };
};

/* ---------- the plates ---------- */

/** The plates' places on Tormo: their face's angle off his facing (1024 a turn), chest, knees, shoulders, back, nape. */
export const PLATE_FACE: readonly number[] = [0, 110, -110, 260, -260, 512, 512];
/** And how high on him (for the drawing: 0 his feet, 1000 his head). */
export const PLATE_HIGH: readonly number[] = [620, 240, 240, 800, 800, 640, 900];

export interface PlatesLayout {
  radius: number;
  rocks: Hex[];
  floor: Hex[];
  tormoAt: Pt;
  naddAt: Pt;
  keeps: { id: string; x: number; y: number }[];
  bows: Pt[];
  start: Pt;
}

/** The order the plates glow in: the front's first (he comes at you), the shoulders, then the back (and the nape with the old dust). */
export const platesOrder = (seed: number, n: number): number[] => {
  const rng = seedRng(deriveSeed(seed, 0x91a7));
  const front = shuffle(rng, [0, 1, 2]);
  const sides = shuffle(rng, [3, 4]);
  const order = [front[0], front[1], sides[0], front[2], sides[1], 5, 6] as number[];
  return order.slice(0, n);
};

export const platesLayout = (): PlatesLayout => {
  const R = PLATES_RADIUS;
  // The yard: a terrace cut in the cliff, the forge's door to the south, the sea below; a few blocks of stone about.
  const blocks: Hex[] = [
    { q: -6, r: 1 },
    { q: 5, r: -6 },
    { q: 6, r: 2 },
  ];
  const solid = new Set(blocks.map(key));
  const floor = allCells(R).filter((h) => hexLen(h.q, h.r) <= R - 1 && !solid.has(key(h)));
  const open = new Set(floor.map(key));
  const rocks = allCells(R + 2).filter((h) => !open.has(key(h)));
  const w = (q: number, r: number): Pt => hexToWorld(q, r);
  const nadd = w(0, 7);
  return {
    radius: R,
    rocks: rocks.sort(sortHex),
    floor: floor.sort(sortHex),
    tormoAt: w(0, -2),
    naddAt: nadd,
    keeps: [
      { id: 'nadd', ...nadd },
      { id: 'dunna', ...w(-2, 7) },
      { id: 'adela', ...w(-1, 6) },
      { id: 'quarry', ...w(1, 5) },
      { id: 'quarry2', ...w(-6, 3) },
      { id: 'quarry3', ...w(7, -2) },
    ],
    bows: [w(-6, 4), w(7, -3), w(-1, -7)],
    start: w(0, 4),
  };
};
